import { ERROR_CODES } from "@shared/types/errors.ts";
import type { ErrorCode, ErrorBody } from "@shared/types/errors.ts";

export type { ErrorCode, ErrorBody };
export { ERROR_CODES };

/** Thrown by the apiClients response interceptor for any non-2xx response. Carries the same
 *  code/message/details the server sent, so callers can switch on `code` instead of parsing
 *  `message` text. */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: ErrorCode,
    public readonly httpStatus: number,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Rate-limit-specific ApiError — kept as a distinct type so call sites can keep their
 *  existing `instanceof RateLimitError` checks; `code` is always ERROR_CODES.RATE_LIMIT.EXCEEDED. */
export class RateLimitError extends ApiError {
  constructor(
    message: string,
    public readonly retryAfter: number | undefined,
    details?: Record<string, unknown>
  ) {
    super(message, ERROR_CODES.RATE_LIMIT.EXCEEDED, 429, details);
    this.name = "RateLimitError";
  }
}

/** Wraps a caught value that isn't already an ApiError (e.g. a client-side token failure that
 *  never reached the server) into one, so callers only ever branch on ApiError. */
export function genericApiError(err: unknown): ApiError {
  const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
  return new ApiError(message, ERROR_CODES.INTERNAL.ERROR, 0);
}

/** Returns true when an error warrants a Sentry capture.
 *  4xx API errors are expected outcomes handled by the UI — the backend already logs what it
 *  considers notable, so capturing them here would produce duplicate events. */
export function isSentryWorthy(err: unknown): boolean {
  if (err instanceof ApiError && err.httpStatus >= 400 && err.httpStatus < 500) return false;
  return true;
}
